<script setup>
import { ref, onMounted, onUnmounted, computed, watch, reactive, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import { useSlotsConfig } from 'dashboard/composables/useSlotsConfig';
import { useSectionPermissions } from 'dashboard/composables/useSectionPermissions';
import { useStore } from 'dashboard/composables/store';
import SlotForm from './components/SlotForm.vue';
import SlotCard from './components/SlotCard.vue';
import TestChatPanel from './components/TestChatPanel.vue';
import FieldFormModal from './components/FieldFormModal.vue';
import AgentBotModal from '../../agentBots/components/AgentBotModal.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import AdminConfigAPI from 'dashboard/api/omni/adminConfig';
import ConversationSlotValuesAPI from 'dashboard/api/omni/conversationSlotValues';
import ConversationFieldValuesAPI from 'dashboard/api/omni/conversationFieldValues';
import BotFieldsAPI from 'dashboard/api/omni/botFields';
import slotsConfigAPI from 'dashboard/api/omni/slotsConfig';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';

const MODAL_TYPES = {
  CREATE: 'create',
  EDIT: 'edit',
};

const { t } = useI18n();
const { accountId } = useAccount();
const { canEditSection } = useSectionPermissions();
const store = useStore();

const {
  slots,
  bots,
  calendars,
  catalogs,
  loading,
  slotsByBot,
  fetchSlots,
  fetchBots,
  fetchCalendars,
  fetchCatalogs,
  createSlot,
  updateSlot,
  deleteSlot,
  removeSlotFile,
} = useSlotsConfig();

// Test conversation ref (populated via TestChatPanel emit)
const testConversation = ref(null);

// Slot values completion status (per slot_config_id)
const slotCompletionStatus = reactive({});

// Field values state (per field_name) — keeps catalog entries with current values
const fieldsState = reactive({});

// UI State
const showForm = ref(false);
const editingSlot = ref(null);
const selectedBotId = ref(null);
const showTestChat = ref(true);

// Bot management state
const botModalType = ref(MODAL_TYPES.CREATE);
const selectedBotForEdit = ref({});
const agentBotModalRef = ref(null);
const agentBotDeleteDialogRef = ref(null);
const botLoading = ref({});

// AI Generate State
const showAIGenerateModal = ref(false);
const aiGenerateDescription = ref('');
const aiGenerateLoading = ref(false);
const aiGenerateElapsed = ref(0);
let aiGenerateTimer = null;

// Delete confirmation modal
const showDeleteModal = ref(false);
const slotToDelete = ref(null);

// Field form modal state
const showFieldFormModal = ref(false);
const editingField = ref(null);
const fieldSaving = ref(false);

// Field delete confirmation
const showFieldDeleteModal = ref(false);
const fieldToDelete = ref(null);
const fieldDeleting = ref(false);

// Selected bot object
const selectedBot = computed(() => {
  if (!selectedBotId.value) return null;
  return bots.value.find(b => b.id === selectedBotId.value);
});

// Check if selected bot is disabled
const isSelectedBotDisabled = computed(() => {
  return selectedBot.value?.disabled_by_plan || false;
});

const selectedBotName = computed(() => selectedBotForEdit.value?.name || '');

// Filtered slots - only show when a bot is selected, sorted by priority
const filteredSlots = computed(() => {
  if (!selectedBotId.value) return [];
  return slots.value
    .filter(s => s.agent_bot_id === selectedBotId.value)
    .sort((a, b) => {
      const priorityA = a.priority ? parseInt(a.priority, 10) : Infinity;
      const priorityB = b.priority ? parseInt(b.priority, 10) : Infinity;
      return priorityA - priorityB;
    });
});

// Check if there are bots available
const hasBots = computed(() => bots.value.length > 0);

// Handle real-time slot restriction compilation updates
const handleSlotRestrictionCompiled = data => {
  const slotIndex = slots.value.findIndex(s => s.id === data.id);
  if (slotIndex !== -1) {
    slots.value[slotIndex] = {
      ...slots.value[slotIndex],
      restriction_status: data.restriction_status,
      restriction_confidence: data.restriction_confidence,
      restriction_needs_review: data.restriction_needs_review,
      restriction_ambiguous: data.restriction_ambiguous,
      restriction_validated: data.restriction_validated,
      restriction_notes: data.restriction_notes,
      restriction_errors: data.restriction_errors,
      restriction_json: data.restriction_json,
    };

    if (editingSlot.value?.id === data.id) {
      editingSlot.value = { ...slots.value[slotIndex] };
    }
  }
};

// Handle real-time slot response instruction compilation updates
const handleSlotResponseInstructionCompiled = data => {
  const slotIndex = slots.value.findIndex(s => s.id === data.id);
  if (slotIndex !== -1) {
    slots.value[slotIndex] = {
      ...slots.value[slotIndex],
      response_instruction_status: data.response_instruction_status,
      response_instruction_confidence: data.response_instruction_confidence,
      response_instruction_needs_review: data.response_instruction_needs_review,
      response_instruction_ambiguous: data.response_instruction_ambiguous,
      response_instruction_validated: data.response_instruction_validated,
      response_instruction_notes: data.response_instruction_notes,
      response_instruction_errors: data.response_instruction_errors,
      response_instruction_json: data.response_instruction_json,
    };

    if (editingSlot.value?.id === data.id) {
      editingSlot.value = { ...slots.value[slotIndex] };
    }
  }
};

// Handle real-time slot value completion updates (from external API)
const handleSlotValueCompleted = data => {
  if (testConversation.value?.id === data.conversation_id ||
      testConversation.value?.display_id === data.conversation_display_id) {
    // Use the status from the event payload; fall back to 'concluded'
    slotCompletionStatus[data.slot_config_id] = data.status || 'concluded';
  }
};

// Handle real-time slot values reset (from reset API)
const handleSlotValuesReset = data => {
  if (testConversation.value?.id === data.conversation_id ||
      testConversation.value?.display_id === data.conversation_display_id) {
    (data.reset_slot_config_ids || []).forEach(slotConfigId => {
      slotCompletionStatus[slotConfigId] = 'not_concluded';
    });
    fetchFieldValues();
  }
};

// Handle real-time slot navigation (from slot_navigate API)
const handleSlotValuesNavigated = data => {
  if (testConversation.value?.id === data.conversation_id ||
      testConversation.value?.display_id === data.conversation_display_id) {
    (data.slots || []).forEach(slot => {
      slotCompletionStatus[slot.slot_config_id] = slot.status || 'not_concluded';
    });
    fetchFieldValues();
  }
};

// Fetch slot values for the test conversation — stores the full state string
const fetchSlotValues = async () => {
  if (!testConversation.value?.id || !selectedBotId.value) return;

  try {
    const { data } = await ConversationSlotValuesAPI.getByConversation(
      testConversation.value.id,
      selectedBotId.value
    );

    // Clear then repopulate with state strings for every returned slot
    Object.keys(slotCompletionStatus).forEach(key => delete slotCompletionStatus[key]);

    (data.slot_values || []).forEach(sv => {
      slotCompletionStatus[sv.slot_config_id] = sv.status || 'not_concluded';
    });
  } catch (error) {
    console.error('Error fetching slot values:', error);
  }
};

// Fields state list (sorted by name) for the sidebar real-time list
const fieldsList = computed(() =>
  Object.values(fieldsState).sort((a, b) =>
    (a.field_name || '').localeCompare(b.field_name || '')
  )
);

// Fetch fields catalog + values for the selected bot (and active test conversation if any)
const fetchFieldValues = async () => {
  if (!selectedBotId.value) {
    Object.keys(fieldsState).forEach(key => delete fieldsState[key]);
    return;
  }

  try {
    const { data } = await ConversationFieldValuesAPI.getByConversation(
      testConversation.value?.id || null,
      selectedBotId.value
    );

    Object.keys(fieldsState).forEach(key => delete fieldsState[key]);
    (data.fields || []).forEach(f => {
      fieldsState[f.field_name] = f;
    });
  } catch (error) {
    console.error('Error fetching field values:', error);
  }
};

// Real-time field value updates from ActionCable
const handleFieldValueUpdated = data => {
  if (data.agent_bot_id && data.agent_bot_id !== selectedBotId.value) return;
  const conv = testConversation.value;
  if (!conv) return;
  if (
    conv.id !== data.conversation_id &&
    conv.display_id !== data.conversation_display_id
  ) {
    return;
  }

  (data.fields || []).forEach(f => {
    const existing = fieldsState[f.field_name] || { field_name: f.field_name };
    fieldsState[f.field_name] = {
      ...existing,
      field_value: f.field_value,
      filled: f.filled,
    };
  });
};

// Watch for test conversation changes to fetch slot values + field values
watch(
  () => testConversation.value?.id,
  async newConvId => {
    if (newConvId) {
      await fetchSlotValues();
    } else {
      // Clear state map when no test conversation — badges will be hidden
      Object.keys(slotCompletionStatus).forEach(key => delete slotCompletionStatus[key]);
    }
    await fetchFieldValues();
  }
);

// Handle conversation updates from TestChatPanel
const handleConversationChanged = conv => {
  testConversation.value = conv;
};

onMounted(async () => {
  await Promise.all([fetchBots(), fetchCalendars(), fetchCatalogs()]);
  if (bots.value.length > 0) {
    selectedBotId.value = bots.value[0].id;
    await fetchSlots(selectedBotId.value);
    await fetchFieldValues();
  }

  emitter.on(
    BUS_EVENTS.SLOT_RESTRICTION_COMPILED,
    handleSlotRestrictionCompiled
  );
  emitter.on(
    BUS_EVENTS.SLOT_RESPONSE_INSTRUCTION_COMPILED,
    handleSlotResponseInstructionCompiled
  );
  emitter.on(
    BUS_EVENTS.KANBAN_SLOT_VALUE_COMPLETED,
    handleSlotValueCompleted
  );
  emitter.on(
    BUS_EVENTS.KANBAN_SLOT_VALUES_RESET,
    handleSlotValuesReset
  );
  emitter.on(
    BUS_EVENTS.KANBAN_SLOT_VALUES_NAVIGATED,
    handleSlotValuesNavigated
  );
  emitter.on(
    BUS_EVENTS.KANBAN_FIELD_VALUE_UPDATED,
    handleFieldValueUpdated
  );
});

onUnmounted(() => {
  emitter.off(
    BUS_EVENTS.SLOT_RESTRICTION_COMPILED,
    handleSlotRestrictionCompiled
  );
  emitter.off(
    BUS_EVENTS.SLOT_RESPONSE_INSTRUCTION_COMPILED,
    handleSlotResponseInstructionCompiled
  );
  emitter.off(
    BUS_EVENTS.KANBAN_SLOT_VALUE_COMPLETED,
    handleSlotValueCompleted
  );
  emitter.off(
    BUS_EVENTS.KANBAN_SLOT_VALUES_RESET,
    handleSlotValuesReset
  );
  emitter.off(
    BUS_EVENTS.KANBAN_SLOT_VALUES_NAVIGATED,
    handleSlotValuesNavigated
  );
  emitter.off(
    BUS_EVENTS.KANBAN_FIELD_VALUE_UPDATED,
    handleFieldValueUpdated
  );
});

const handleSelectBot = async botId => {
  selectedBotId.value = botId;
  await fetchSlots(botId);
  await fetchFieldValues();
};

const handleNewSlot = () => {
  if (!selectedBotId.value) return;
  if (isSelectedBotDisabled.value) {
    useAlert(t('OMNI.SLOTS.BOT_DISABLED_MESSAGE'));
    return;
  }
  editingSlot.value = { agent_bot_id: selectedBotId.value };
  showForm.value = true;
};

const handleEditSlot = slot => {
  editingSlot.value = slot;
  showForm.value = true;
};

const handleSaveSlot = async ({ data: slotData, file: actionFile }) => {
  let saved;
  if (editingSlot.value?.id) {
    saved = await updateSlot(editingSlot.value.id, slotData, actionFile);
  } else {
    saved = await createSlot(slotData, actionFile);
  }

  // For api_call slots, keep the modal open after save so the user can
  // continue configuring response-based automations. The automations panel
  // only renders once the slot has a persisted id, so we sync editingSlot
  // to the freshly saved record to make it appear.
  if (saved && saved.action_type === 'api_call') {
    editingSlot.value = { ...saved };
    return;
  }

  showForm.value = false;
  editingSlot.value = null;
};

const handleRemoveFile = async slotId => {
  if (!slotId) return;
  await removeSlotFile(slotId);
  // Update editingSlot to reflect the removed file
  if (editingSlot.value?.id === slotId) {
    editingSlot.value = {
      ...editingSlot.value,
      action_file_name: null,
      action_file_url: null,
      action_file_content_type: null,
      action_file_size: null,
    };
  }
};

const handleDeleteSlot = slot => {
  slotToDelete.value = slot;
  showDeleteModal.value = true;
};

const confirmDeleteSlot = async () => {
  if (slotToDelete.value) {
    await deleteSlot(slotToDelete.value.id);
    showDeleteModal.value = false;
    slotToDelete.value = null;
  }
};

const cancelDeleteSlot = () => {
  showDeleteModal.value = false;
  slotToDelete.value = null;
};

// === Field management ===
const handleNewField = () => {
  if (!selectedBotId.value) return;
  if (isSelectedBotDisabled.value) {
    useAlert(t('OMNI.SLOTS.BOT_DISABLED_MESSAGE'));
    return;
  }
  editingField.value = null;
  showFieldFormModal.value = true;
};

const handleEditField = field => {
  if (isSelectedBotDisabled.value) {
    useAlert(t('OMNI.SLOTS.BOT_DISABLED_MESSAGE'));
    return;
  }
  editingField.value = field;
  showFieldFormModal.value = true;
};

const closeFieldFormModal = () => {
  if (fieldSaving.value) return;
  showFieldFormModal.value = false;
  editingField.value = null;
};

const handleSaveField = async payload => {
  fieldSaving.value = true;
  try {
    if (payload.isEdit) {
      const updateBody = {
        agentBotId: selectedBotId.value,
        description: payload.description,
        possibleValues: payload.possibleValues,
      };
      if (!payload.nameLocked && payload.name !== payload.originalName) {
        updateBody.newName = payload.name;
      }
      await BotFieldsAPI.update(payload.originalName, updateBody);
    } else {
      await BotFieldsAPI.create({
        agentBotId: selectedBotId.value,
        name: payload.name,
        description: payload.description,
        possibleValues: payload.possibleValues,
      });
    }
    useAlert(t('OMNI.SLOTS.FIELD_SAVED'));
    showFieldFormModal.value = false;
    editingField.value = null;
    await fetchFieldValues();
  } catch (error) {
    const message =
      error?.response?.data?.error || t('OMNI.SLOTS.FIELD_SAVE_ERROR');
    useAlert(message);
  } finally {
    fieldSaving.value = false;
  }
};

const handleDeleteField = field => {
  if (!field?.deletable) return;
  if (isSelectedBotDisabled.value) {
    useAlert(t('OMNI.SLOTS.BOT_DISABLED_MESSAGE'));
    return;
  }
  fieldToDelete.value = field;
  showFieldDeleteModal.value = true;
};

const cancelDeleteField = () => {
  if (fieldDeleting.value) return;
  showFieldDeleteModal.value = false;
  fieldToDelete.value = null;
};

const confirmDeleteField = async () => {
  if (!fieldToDelete.value) return;
  fieldDeleting.value = true;
  try {
    await BotFieldsAPI.destroy(fieldToDelete.value.field_name, selectedBotId.value);
    useAlert(t('OMNI.SLOTS.FIELD_DELETED'));
    showFieldDeleteModal.value = false;
    fieldToDelete.value = null;
    await fetchFieldValues();
  } catch (error) {
    const message =
      error?.response?.data?.error || t('OMNI.SLOTS.FIELD_DELETE_ERROR');
    useAlert(message);
  } finally {
    fieldDeleting.value = false;
  }
};

const toggleTestChat = () => {
  showTestChat.value = !showTestChat.value;
};

// Bot management functions
const openAddBotModal = async () => {
  botModalType.value = MODAL_TYPES.CREATE;
  selectedBotForEdit.value = {};
  await nextTick();
  agentBotModalRef.value.dialogRef.open();
};

const openEditBotModal = async (bot, event) => {
  event.stopPropagation();
  botModalType.value = MODAL_TYPES.EDIT;
  selectedBotForEdit.value = bot;
  await nextTick();
  agentBotModalRef.value.dialogRef.open();
};

const openDeleteBotPopup = (bot, event) => {
  event.stopPropagation();
  selectedBotForEdit.value = bot;
  agentBotDeleteDialogRef.value.open();
};

const deleteAgentBot = async id => {
  try {
    await store.dispatch('agentBots/delete', id);
    useAlert(t('AGENT_BOTS.DELETE.API.SUCCESS_MESSAGE'));
    // Refresh bots list
    await fetchBots();
    // If deleted bot was selected, select first available
    if (selectedBotId.value === id) {
      selectedBotId.value = bots.value.length > 0 ? bots.value[0].id : null;
      if (selectedBotId.value) {
        await fetchSlots(selectedBotId.value);
      }
    }
  } catch (error) {
    useAlert(t('AGENT_BOTS.DELETE.API.ERROR_MESSAGE'));
  } finally {
    botLoading.value[id] = false;
    selectedBotForEdit.value = {};
  }
};

const confirmBotDeletion = () => {
  botLoading.value[selectedBotForEdit.value.id] = true;
  deleteAgentBot(selectedBotForEdit.value.id);
  agentBotDeleteDialogRef.value.close();
};

const handleBotSaved = async () => {
  // Refresh bots list after create/edit
  await fetchBots();
  // If it's a new bot, select it
  if (botModalType.value === MODAL_TYPES.CREATE && bots.value.length > 0) {
    const newBot = bots.value[bots.value.length - 1];
    selectedBotId.value = newBot.id;
    await fetchSlots(newBot.id);
  }
};

const handleBotUpdateFromOmni = async (botId, botData) => {
  const response = await slotsConfigAPI.updateBot(botId, {
    name: botData.name,
    bot_profile: botData.bot_profile,
    restrictions: botData.restrictions,
    fallback_action: typeof botData.fallback_action === 'object'
      ? JSON.stringify(botData.fallback_action)
      : botData.fallback_action,
    fallback_agent_phone: botData.fallback_agent_phone || '',
    bot_flow_model_id: botData.bot_flow_model_id,
  });
  return response.data;
};

// AI Generate functions
const openAIGenerateModal = () => {
  aiGenerateDescription.value = '';
  showAIGenerateModal.value = true;
};

const closeAIGenerateModal = () => {
  showAIGenerateModal.value = false;
  aiGenerateDescription.value = '';
};

const startElapsedTimer = () => {
  aiGenerateElapsed.value = 0;
  aiGenerateTimer = setInterval(() => {
    aiGenerateElapsed.value += 1;
  }, 1000);
};

const stopElapsedTimer = () => {
  if (aiGenerateTimer) {
    clearInterval(aiGenerateTimer);
    aiGenerateTimer = null;
  }
};

const formatElapsedTime = seconds => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  if (mins > 0) {
    return `${mins}m ${secs}s`;
  }
  return `${secs}s`;
};

const handleAIGenerate = async () => {
  if (!aiGenerateDescription.value.trim() || !selectedBotId.value) {
    useAlert(t('OMNI.AI_GENERATE.VALIDATION_ERROR'));
    return;
  }

  aiGenerateLoading.value = true;
  startElapsedTimer();

  try {
    const configResponse = await AdminConfigAPI.get('ai_generate_webhook');
    const webhookUrl = configResponse.data?.value;

    if (!webhookUrl) {
      useAlert(t('OMNI.AI_GENERATE.WEBHOOK_NOT_CONFIGURED'));
      aiGenerateLoading.value = false;
      stopElapsedTimer();
      return;
    }

    const url = `${webhookUrl}?bot_id=${selectedBotId.value}&account_id=${accountId.value}`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        description: aiGenerateDescription.value,
        bot_id: selectedBotId.value,
        bot_name: selectedBot.value?.name || '',
        account_id: accountId.value,
      }),
    });

    stopElapsedTimer();

    if (response.ok) {
      useAlert(t('OMNI.AI_GENERATE.SUCCESS'));
      await fetchSlots(selectedBotId.value);
      closeAIGenerateModal();
    } else {
      useAlert(t('OMNI.AI_GENERATE.ERROR'));
    }
  } catch (error) {
    console.error('AI Generate error:', error);
    useAlert(t('OMNI.AI_GENERATE.ERROR'));
    stopElapsedTimer();
  } finally {
    aiGenerateLoading.value = false;
  }
};
</script>

<template>
  <div class="flex flex-col h-full">
    <!-- Header -->
    <div
      class="flex items-center justify-between p-4 border-b border-slate-75 dark:border-slate-800"
    >
      <h1 class="text-xl font-semibold text-slate-900 dark:text-slate-25">
        {{ t('OMNI.SLOTS.HEADER') }}
      </h1>
      <div class="flex gap-2">
        <!-- Toggle Test Chat -->
        <button
          v-if="selectedBotId"
          class="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md transition-colors"
          :class="
            showTestChat
              ? 'text-woot-600 bg-woot-50 dark:bg-woot-900/20 hover:bg-woot-100 dark:hover:bg-woot-900/30'
              : 'text-slate-600 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700'
          "
          @click="toggleTestChat"
        >
          <span class="i-lucide-message-circle" />
          {{ t('OMNI.TEST_CHAT.TOGGLE') }}
        </button>

        <button
          v-if="selectedBotId && canEditSection('stages')"
          v-tooltip.top="isSelectedBotDisabled ? 'Bot desabilitado - faça upgrade para criar slots' : ''"
          class="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white bg-woot-500 rounded-md hover:bg-woot-600 disabled:opacity-50 disabled:cursor-not-allowed"
          :disabled="!selectedBotId || isSelectedBotDisabled"
          @click="handleNewSlot"
        >
          <span class="i-lucide-plus" />
          {{ t('OMNI.SLOTS.NEW_SLOT') }}
        </button>
      </div>
    </div>

    <!-- Main Content -->
    <div class="flex-1 overflow-hidden p-4">
      <!-- Content -->
      <div class="flex gap-4 h-full">
        <!-- Bots Sidebar -->
        <div
          class="w-64 bg-white dark:bg-slate-900 rounded-lg border border-slate-75 dark:border-slate-800 p-4 flex flex-col overflow-hidden flex-shrink-0"
        >
          <!-- Header with Add Button -->
          <div class="flex items-center justify-between mb-4">
            <h2 class="text-lg font-semibold text-slate-900 dark:text-slate-25">
              {{ t('OMNI.SLOTS.BOTS') }}
            </h2>
            <button
              v-if="canEditSection('stages')"
              v-tooltip.top="t('AGENT_BOTS.ADD.TITLE')"
              class="p-1.5 rounded-lg text-woot-600 hover:bg-woot-50 dark:hover:bg-woot-900/20 transition-colors"
              @click="openAddBotModal"
            >
              <span class="i-lucide-plus size-5" />
            </button>
          </div>

          <!-- Empty state -->
          <div
            v-if="!hasBots"
            class="flex-1 flex flex-col items-center justify-center text-center p-4"
          >
            <span
              class="i-lucide-bot text-4xl text-slate-300 dark:text-slate-600 mb-3"
            />
            <p class="text-sm text-slate-500 dark:text-slate-400 mb-4">
              {{ t('OMNI.SLOTS.NO_BOTS_MESSAGE') }}
            </p>
            <button
              v-if="canEditSection('stages')"
              class="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white bg-woot-500 rounded-md hover:bg-woot-600"
              @click="openAddBotModal"
            >
              <span class="i-lucide-plus" />
              {{ t('AGENT_BOTS.ADD.TITLE') }}
            </button>
          </div>

          <!-- Bot List (max 3 visible, scroll if more) -->
          <div v-else class="max-h-44 overflow-y-auto space-y-1 flex-shrink-0">
            <div
              v-for="bot in bots"
              :key="bot.id"
              class="group relative w-full text-left px-3 py-2.5 rounded-lg transition-colors cursor-pointer"
              :class="[
                selectedBotId === bot.id
                  ? 'bg-woot-50 dark:bg-woot-900/20 text-woot-600'
                  : 'hover:bg-slate-50 dark:hover:bg-slate-800',
                bot.disabled_by_plan ? 'opacity-60' : ''
              ]"
              @click="handleSelectBot(bot.id)"
            >
              <div class="flex items-center justify-between">
                <div class="flex items-center gap-2 min-w-0 flex-1">
                  <span 
                    class="size-4 flex-shrink-0"
                    :class="bot.disabled_by_plan ? 'i-lucide-bot-off text-ruby-500' : 'i-lucide-bot'"
                  />
                  <span class="text-sm font-medium truncate">{{
                    bot.name
                  }}</span>
                  <!-- Disabled badge -->
                  <span
                    v-if="bot.disabled_by_plan"
                    class="inline-flex items-center px-1.5 py-0.5 text-xs font-medium rounded bg-ruby-100 dark:bg-ruby-900/30 text-ruby-700 dark:text-ruby-300"
                  >
                    Desabilitado
                  </span>
                </div>
                <!-- Action buttons -->
                <div
                  v-if="canEditSection('stages')"
                  class="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <button
                    v-if="!bot.system_bot"
                    v-tooltip.top="t('AGENT_BOTS.EDIT.BUTTON_TEXT')"
                    class="p-1 rounded text-slate-500 hover:text-woot-600 hover:bg-woot-50 dark:hover:bg-woot-900/30"
                    @click="openEditBotModal(bot, $event)"
                  >
                    <span class="i-lucide-pen size-3.5" />
                  </button>
                  <button
                    v-if="!bot.system_bot"
                    v-tooltip.top="t('AGENT_BOTS.DELETE.BUTTON_TEXT')"
                    class="p-1 rounded text-slate-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30"
                    @click="openDeleteBotPopup(bot, $event)"
                  >
                    <span class="i-lucide-trash-2 size-3.5" />
                  </button>
                </div>
              </div>
              <span
                v-if="slotsByBot[bot.id]"
                class="block ml-6 text-xs text-slate-500 mt-0.5"
              >
                {{ slotsByBot[bot.id]?.length || 0 }}
                {{ t('OMNI.SLOTS.SLOTS_COUNT') }}
              </span>
            </div>
          </div>

          <!-- Fields State (real-time) -->
          <div
            v-if="selectedBotId"
            class="mt-4 pt-4 border-t border-slate-75 dark:border-slate-800 flex-1 overflow-hidden flex flex-col min-h-0"
          >
            <div class="flex items-center justify-between mb-2">
              <h3
                class="text-sm font-semibold text-slate-700 dark:text-slate-200"
              >
                {{ t('OMNI.SLOTS.FIELDS_STATE') }}
              </h3>
              <button
                v-if="canEditSection('stages')"
                v-tooltip.top="t('OMNI.SLOTS.ADD_FIELD')"
                class="p-1 rounded text-slate-500 hover:text-woot-600 hover:bg-woot-50 dark:hover:bg-woot-900/30 transition-colors"
                :disabled="isSelectedBotDisabled"
                @click="handleNewField"
              >
                <span class="i-lucide-plus size-4" />
              </button>
            </div>
            <div
              v-if="fieldsList.length === 0"
              class="text-xs text-slate-500 dark:text-slate-400 italic"
            >
              {{ t('OMNI.SLOTS.NO_FIELDS') }}
            </div>
            <ul v-else class="flex-1 overflow-y-auto space-y-1 text-xs">
              <li
                v-for="f in fieldsList"
                :key="f.field_name"
                class="group px-2 py-1.5 rounded hover:bg-slate-50 dark:hover:bg-slate-800"
              >
                <div class="flex items-center justify-between gap-2">
                  <span
                    class="font-mono truncate text-slate-700 dark:text-slate-300"
                    :title="f.description || f.field_name"
                  >
                    {{ f.field_name }}
                  </span>
                  <div
                    v-if="canEditSection('stages')"
                    class="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                  >
                    <button
                      v-tooltip.top="t('OMNI.SLOTS.EDIT_FIELD')"
                      class="p-1 rounded text-slate-500 hover:text-woot-600 hover:bg-woot-50 dark:hover:bg-woot-900/30"
                      @click="handleEditField(f)"
                    >
                      <span class="i-lucide-pen size-3.5" />
                    </button>
                    <button
                      v-tooltip.top="
                        f.deletable
                          ? t('OMNI.SLOTS.DELETE_FIELD')
                          : t('OMNI.SLOTS.DELETE_FIELD_DISABLED')
                      "
                      class="p-1 rounded text-slate-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 disabled:hover:text-slate-300 dark:disabled:hover:text-slate-600 disabled:hover:bg-transparent disabled:cursor-not-allowed disabled:opacity-50"
                      :disabled="!f.deletable"
                      @click="handleDeleteField(f)"
                    >
                      <span class="i-lucide-trash-2 size-3.5" />
                    </button>
                  </div>
                </div>
                <div class="mt-0.5">
                  <span
                    v-if="f.filled"
                    class="inline-block px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 truncate max-w-full"
                    :title="f.field_value"
                  >
                    {{ f.field_value }}
                  </span>
                  <span
                    v-else
                    class="inline-block px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400 italic"
                  >
                    {{ t('OMNI.SLOTS.FIELD_VALUE_NULL') }}
                  </span>
                </div>
              </li>
            </ul>
          </div>
        </div>

        <!-- Slots List -->
        <div
          class="flex-1 bg-white dark:bg-slate-900 rounded-lg border border-slate-75 dark:border-slate-800 p-4 flex flex-col overflow-hidden min-w-0"
        >
          <!-- Header with selected bot name -->
          <div class="flex items-center justify-between mb-4">
            <h2 class="text-lg font-semibold text-slate-900 dark:text-slate-25">
              <template v-if="selectedBot">
                {{ selectedBot.name }} - {{ t('OMNI.SLOTS.SLOTS') }}
              </template>
              <template v-else>
                {{ t('OMNI.SLOTS.SELECT_BOT') }}
              </template>
            </h2>
          </div>

          <!-- Disabled bot warning -->
          <div
            v-if="isSelectedBotDisabled"
            class="mb-4 flex items-center gap-3 p-4 rounded-lg bg-ruby-50 dark:bg-ruby-900/20 border border-ruby-200 dark:border-ruby-800"
          >
            <span class="i-lucide-alert-triangle size-6 text-ruby-600 dark:text-ruby-400 flex-shrink-0" />
            <div class="flex-1">
              <span class="block text-sm font-medium text-ruby-700 dark:text-ruby-300">
                Bot Desabilitado
              </span>
              <span class="text-xs text-ruby-600 dark:text-ruby-400">
                Este bot foi desabilitado por exceder o limite do plano. Você pode visualizar os slots existentes, mas não pode criar novos. Faça upgrade do seu plano para reativar o bot.
              </span>
            </div>
            <router-link
              :to="{ name: 'omni_billing' }"
              class="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-ruby-600 hover:bg-ruby-700 rounded-lg transition-colors"
            >
              <span class="i-lucide-rocket size-4" />
              Upgrade
            </router-link>
          </div>

          <!-- No bot selected -->
          <div
            v-if="!selectedBotId"
            class="flex-1 flex flex-col items-center justify-center text-center p-4"
          >
            <span
              class="i-lucide-arrow-left text-4xl text-slate-300 dark:text-slate-600 mb-2"
            />
            <p class="text-sm text-slate-600 dark:text-slate-400">
              {{ t('OMNI.SLOTS.SELECT_BOT_MESSAGE') }}
            </p>
          </div>

          <!-- Loading -->
          <div
            v-else-if="loading"
            class="flex-1 flex items-center justify-center"
          >
            <span class="i-lucide-loader-2 w-8 h-8 animate-spin text-woot-500" />
          </div>

          <!-- Empty state -->
          <div
            v-else-if="filteredSlots.length === 0"
            class="flex-1 flex flex-col items-center justify-center text-center p-4"
          >
            <span
              class="i-lucide-sparkles text-5xl text-woot-400 dark:text-woot-500 mb-4"
            />
            <h3
              class="text-lg font-semibold text-slate-800 dark:text-slate-200 mb-2"
            >
              {{ t('OMNI.AI_GENERATE.EMPTY_TITLE') }}
            </h3>
            <p class="text-sm text-slate-600 dark:text-slate-400 mb-6 max-w-md">
              {{ t('OMNI.AI_GENERATE.EMPTY_DESCRIPTION') }}
            </p>

            <!-- AI Generate Button (Primary) -->
            <button
              v-if="canEditSection('stages')"
              class="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-woot-500 to-woot-600 rounded-lg hover:from-woot-600 hover:to-woot-700 shadow-md hover:shadow-lg transition-all mb-3"
              @click="openAIGenerateModal"
            >
              <span class="i-lucide-sparkles" />
              {{ t('OMNI.AI_GENERATE.BUTTON') }}
            </button>

            <!-- Manual Create (Secondary) -->
            <button
              v-if="canEditSection('stages')"
              class="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 transition-colors"
              @click="handleNewSlot"
            >
              <span class="i-lucide-plus" />
              {{ t('OMNI.SLOTS.CREATE_MANUAL') }}
            </button>
          </div>

          <!-- Slots grid -->
          <div v-else class="flex-1 overflow-y-auto">
            <div class="grid grid-cols-1 xl:grid-cols-2 gap-3">
              <SlotCard
                v-for="slot in filteredSlots"
                :key="slot.id"
                :slot="slot"
                :slot-state="testConversation ? (slotCompletionStatus[slot.id] ?? 'not_concluded') : null"
                @edit="handleEditSlot"
                @delete="handleDeleteSlot"
              />
            </div>
          </div>
        </div>

        <!-- Test Chat Panel -->
        <div
          v-if="showTestChat && selectedBotId"
          class="w-80 flex-shrink-0 transition-all duration-300"
        >
          <TestChatPanel :bot="selectedBot" @conversation-changed="handleConversationChanged" />
        </div>
      </div>
    </div>

    <!-- Slot Form Modal -->
    <SlotForm
      :slot="editingSlot"
      :is-open="showForm"
      :bots="bots"
      :selected-bot-id="selectedBotId"
      :calendars="calendars"
      :catalogs="catalogs"
      :all-slots="filteredSlots"
      @save="handleSaveSlot"
      @close="showForm = false"
      @remove-file="handleRemoveFile"
    />

    <!-- Field Form Modal -->
    <FieldFormModal
      :is-open="showFieldFormModal"
      :field="editingField"
      :saving="fieldSaving"
      @save="handleSaveField"
      @close="closeFieldFormModal"
    />

    <!-- Field Delete Confirmation Modal -->
    <Teleport to="body">
      <div
        v-if="showFieldDeleteModal"
        class="fixed inset-0 z-50 flex items-center justify-center"
      >
        <div
          class="absolute inset-0 bg-black/50 backdrop-blur-sm"
          @click="cancelDeleteField"
        />
        <div
          class="relative bg-white dark:bg-slate-900 rounded-lg p-6 max-w-md mx-4 shadow-xl"
        >
          <div class="flex items-center gap-3 mb-4">
            <div
              class="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center"
            >
              <span
                class="i-lucide-alert-triangle text-red-600 dark:text-red-400 text-xl"
              />
            </div>
            <h4 class="text-lg font-semibold text-slate-900 dark:text-slate-100">
              {{ t('OMNI.SLOTS.DELETE_FIELD_CONFIRM_TITLE') }}
            </h4>
          </div>
          <p class="text-sm text-slate-600 dark:text-slate-400 mb-4">
            {{
              t('OMNI.SLOTS.DELETE_FIELD_CONFIRM', {
                name: fieldToDelete?.field_name,
              })
            }}
          </p>
          <div class="flex gap-2 justify-end">
            <button
              class="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-700"
              :disabled="fieldDeleting"
              @click="cancelDeleteField"
            >
              {{ t('OMNI.COMMON.CANCEL') }}
            </button>
            <button
              class="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-red-500 rounded-lg hover:bg-red-600 disabled:bg-red-300 disabled:cursor-not-allowed"
              :disabled="fieldDeleting"
              @click="confirmDeleteField"
            >
              <span
                v-if="fieldDeleting"
                class="i-lucide-loader-2 size-4 animate-spin"
              />
              {{ t('OMNI.COMMON.DELETE') }}
            </button>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Agent Bot Modal -->
    <AgentBotModal
      ref="agentBotModalRef"
      :type="botModalType"
      :selected-bot="selectedBotForEdit"
      :on-update="botModalType === 'edit' ? handleBotUpdateFromOmni : undefined"
      @saved="handleBotSaved"
    />

    <!-- Agent Bot Delete Dialog -->
    <Dialog
      ref="agentBotDeleteDialogRef"
      type="alert"
      :title="t('AGENT_BOTS.DELETE.CONFIRM.TITLE')"
      :description="
        t('AGENT_BOTS.DELETE.CONFIRM.MESSAGE', { name: selectedBotName })
      "
      :is-loading="botLoading[selectedBotForEdit.id]"
      :confirm-button-label="t('AGENT_BOTS.DELETE.CONFIRM.YES')"
      :cancel-button-label="t('AGENT_BOTS.DELETE.CONFIRM.NO')"
      @confirm="confirmBotDeletion"
    />

    <!-- Slot Delete Confirmation Modal -->
    <Teleport to="body">
      <div
        v-if="showDeleteModal"
        class="fixed inset-0 z-50 flex items-center justify-center"
      >
        <!-- Backdrop -->
        <div
          class="absolute inset-0 bg-black/50 backdrop-blur-sm"
          @click="cancelDeleteSlot"
        />

        <!-- Modal -->
        <div
          class="relative bg-white dark:bg-slate-900 rounded-lg p-6 max-w-md mx-4 shadow-xl"
        >
          <div class="flex items-center gap-3 mb-4">
            <div
              class="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center"
            >
              <span
                class="i-lucide-alert-triangle text-red-600 dark:text-red-400 text-xl"
              />
            </div>
            <h4 class="text-lg font-semibold text-slate-900 dark:text-slate-100">
              {{ t('OMNI.SLOTS.CONFIRM_DELETE_TITLE') }}
            </h4>
          </div>

          <div class="mb-4">
            <p class="text-sm text-slate-600 dark:text-slate-400 mb-3">
              {{ t('OMNI.SLOTS.CONFIRM_DELETE') }}
            </p>

            <div
              v-if="slotToDelete"
              class="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg"
            >
              <div class="flex items-center gap-2 mb-1">
                <span class="i-lucide-layers text-slate-400" />
                <span class="font-medium text-slate-900 dark:text-slate-100">
                  {{ slotToDelete.slot_name }}
                </span>
                <span
                  v-if="slotToDelete.priority"
                  class="ml-auto w-6 h-6 flex items-center justify-center bg-woot-500 text-white text-xs font-bold rounded"
                >
                  {{ slotToDelete.priority }}
                </span>
              </div>
              <p
                v-if="slotToDelete.slot_description"
                class="text-xs text-slate-500 dark:text-slate-400 ml-6"
              >
                {{ slotToDelete.slot_description }}
              </p>
            </div>
          </div>

          <div class="flex gap-2 justify-end">
            <button
              class="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-700"
              @click="cancelDeleteSlot"
            >
              {{ t('OMNI.COMMON.CANCEL') }}
            </button>
            <button
              class="px-4 py-2 text-sm font-medium text-white bg-red-500 rounded-lg hover:bg-red-600"
              @click="confirmDeleteSlot"
            >
              {{ t('OMNI.COMMON.DELETE') }}
            </button>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- AI Generate Modal -->
    <Teleport to="body">
      <div
        v-if="showAIGenerateModal"
        class="fixed inset-0 z-50 flex items-center justify-center"
      >
        <!-- Backdrop -->
        <div
          class="absolute inset-0 bg-black/50 backdrop-blur-sm"
          @click="closeAIGenerateModal"
        />

        <!-- Modal -->
        <div
          class="relative w-full max-w-2xl mx-4 bg-white dark:bg-slate-900 rounded-xl shadow-2xl"
        >
          <!-- Header -->
          <div
            class="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700"
          >
            <div class="flex items-center gap-3">
              <span class="i-lucide-sparkles text-2xl text-woot-500" />
              <div>
                <h2
                  class="text-lg font-semibold text-slate-900 dark:text-slate-100"
                >
                  {{ t('OMNI.AI_GENERATE.MODAL_TITLE') }}
                </h2>
                <p class="text-sm text-slate-500 dark:text-slate-400">
                  {{ t('OMNI.AI_GENERATE.MODAL_SUBTITLE') }}
                </p>
              </div>
            </div>
            <button
              class="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              @click="closeAIGenerateModal"
            >
              <span class="i-lucide-x text-xl text-slate-500" />
            </button>
          </div>

          <!-- Body -->
          <div class="px-6 py-5">
            <!-- Normal input view -->
            <div v-if="!aiGenerateLoading">
              <label
                class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2"
              >
                {{ t('OMNI.AI_GENERATE.LABEL') }}
              </label>
              <textarea
                v-model="aiGenerateDescription"
                :placeholder="t('OMNI.AI_GENERATE.PLACEHOLDER')"
                class="w-full h-48 px-4 py-3 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:ring-2 focus:ring-woot-500 focus:border-woot-500 resize-none"
              />
              <p class="mt-2 text-xs text-slate-500 dark:text-slate-400">
                {{ t('OMNI.AI_GENERATE.HELP_TEXT') }}
              </p>
            </div>

            <!-- Loading/Generating view -->
            <div
              v-else
              class="flex flex-col items-center justify-center py-12"
            >
              <!-- Animated sparkles icon -->
              <div class="relative mb-6">
                <span
                  class="i-lucide-sparkles text-5xl text-woot-500 animate-pulse"
                />
                <span
                  class="absolute -top-1 -right-1 i-lucide-sparkle text-xl text-woot-400 animate-bounce"
                />
                <span
                  class="absolute -bottom-1 -left-1 i-lucide-sparkle text-lg text-woot-300 animate-bounce"
                  style="animation-delay: 0.2s"
                />
              </div>

              <!-- Animated loading spinner with elapsed time -->
              <div class="relative w-24 h-24 mb-4">
                <!-- Spinning ring -->
                <svg
                  class="w-full h-full animate-spin"
                  style="animation-duration: 3s"
                >
                  <circle
                    cx="48"
                    cy="48"
                    r="42"
                    stroke="currentColor"
                    stroke-width="6"
                    fill="none"
                    class="text-slate-200 dark:text-slate-700"
                  />
                  <circle
                    cx="48"
                    cy="48"
                    r="42"
                    stroke="currentColor"
                    stroke-width="6"
                    fill="none"
                    class="text-woot-500"
                    stroke-dasharray="66 198"
                    stroke-linecap="round"
                  />
                </svg>
                <!-- Elapsed time -->
                <div class="absolute inset-0 flex items-center justify-center">
                  <span
                    class="text-lg font-bold text-woot-600 dark:text-woot-400"
                  >
                    {{ formatElapsedTime(aiGenerateElapsed) }}
                  </span>
                </div>
              </div>

              <h3
                class="text-lg font-semibold text-slate-800 dark:text-slate-200 mb-2"
              >
                {{ t('OMNI.AI_GENERATE.GENERATING_TITLE') }}
              </h3>
              <p
                class="text-sm text-slate-500 dark:text-slate-400 text-center max-w-sm"
              >
                {{ t('OMNI.AI_GENERATE.GENERATING_DESCRIPTION') }}
              </p>

              <!-- Progress dots animation -->
              <div class="flex gap-1 mt-4">
                <span
                  class="w-2 h-2 rounded-full bg-woot-500 animate-bounce"
                  style="animation-delay: 0s"
                />
                <span
                  class="w-2 h-2 rounded-full bg-woot-500 animate-bounce"
                  style="animation-delay: 0.2s"
                />
                <span
                  class="w-2 h-2 rounded-full bg-woot-500 animate-bounce"
                  style="animation-delay: 0.4s"
                />
              </div>
            </div>
          </div>

          <!-- Footer -->
          <div
            class="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 rounded-b-xl"
          >
            <button
              v-if="!aiGenerateLoading"
              class="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
              @click="closeAIGenerateModal"
            >
              {{ t('OMNI.AI_GENERATE.CANCEL') }}
            </button>
            <button
              class="inline-flex items-center gap-2 px-5 py-2 text-sm font-medium text-white rounded-lg transition-all min-w-[180px] justify-center"
              :class="
                aiGenerateLoading
                  ? 'bg-woot-400 cursor-not-allowed'
                  : 'bg-gradient-to-r from-woot-500 to-woot-600 hover:from-woot-600 hover:to-woot-700'
              "
              :disabled="aiGenerateLoading || !aiGenerateDescription.trim()"
              @click="handleAIGenerate"
            >
              <span v-if="aiGenerateLoading" class="i-lucide-loader-2 animate-spin" />
              <span v-else class="i-lucide-send" />
              <template v-if="aiGenerateLoading">
                {{ t('OMNI.AI_GENERATE.GENERATING') }} ({{
                  formatElapsedTime(aiGenerateElapsed)
                }})
              </template>
              <template v-else>
                {{ t('OMNI.AI_GENERATE.SEND') }}
              </template>
            </button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>
